import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { defaultSpatial, reducedFade } from '../motion/tokens'
import { Symbol } from './Symbol'

export type ToastTyp = 'info' | 'error' | 'erfolg'

export interface ToastOptionen {
  typ?: ToastTyp
  dauer?: number
}

export type ToastFunktion = (
  text: string,
  optionenOderTyp?: ToastOptionen | ToastTyp,
) => void

interface Meldung {
  id: number
  text: string
  typ: ToastTyp
  dauer: number
}

const Kontext = createContext<ToastFunktion | null>(null)

/**
 * Erkennt automatisch, ob ein Text ein Verbot oder Fehler darstellt.
 */
function istFehlerText(text: string): boolean {
  const t = text.toLowerCase()
  return (
    t.includes('nicht') ||
    t.includes('kein') ||
    t.includes('fehler') ||
    t.includes('fehlgeschlagen') ||
    t.includes('gesperrt') ||
    t.includes('verboten') ||
    t.includes('ungültig')
  )
}

/**
 * Toast / Snackbar nach Material 3 Expressive:
 *
 * Kurze, hochgradig sichtbare Rückmeldung am Bildschirmrand.
 *
 * Barrierefreiheit:
 * - Bei Fehlern: role="alert", aria-live="assertive" (sofortige Wahrnehmung)
 * - Bei Bestätigungen: role="status", aria-live="polite" (stört den Lesefluss nicht)
 * - Durch Antippen / Klick sofort schließbar
 */
export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [meldung, setMeldung] = useState<Meldung | null>(null)
  const ruhig = useReducedMotion()

  const zeige: ToastFunktion = useCallback(
    (text: string, optionenOderTyp?: ToastOptionen | ToastTyp) => {
      const optionen =
        typeof optionenOderTyp === 'string'
          ? { typ: optionenOderTyp }
          : optionenOderTyp ?? {}

      const typ: ToastTyp =
        optionen.typ ?? (istFehlerText(text) ? 'error' : 'info')
      const standardDauer = typ === 'error' ? 6000 : 4000
      const dauer = optionen.dauer ?? standardDauer

      setMeldung({
        id: Date.now(),
        text,
        typ,
        dauer,
      })
    },
    [],
  )

  useEffect(() => {
    if (!meldung) return
    const uhr = setTimeout(() => {
      setMeldung(null)
    }, meldung.dauer)
    return () => {
      clearTimeout(uhr)
    }
  }, [meldung])

  return (
    <Kontext.Provider value={zeige}>
      {children}
      <div className="snack-bereich" role="status" aria-live="polite">
        <AnimatePresence>
          {meldung ? (
            <motion.div
              key={meldung.id}
              className={`snack snack--${meldung.typ}`}
              onClick={() => setMeldung(null)}
              title="Zum Ausblenden antippen"
              initial={ruhig ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={ruhig ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.95 }}
              transition={ruhig ? reducedFade : defaultSpatial}
            >
              <span className={`snack__icon snack__icon--${meldung.typ}`} aria-hidden="true">
                <Symbol name={meldung.typ === 'error' ? 'close' : meldung.typ === 'erfolg' ? 'check' : 'more'} />
              </span>
              <span className="snack__text">{meldung.text}</span>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </Kontext.Provider>
  )
}

/** Meldet eine kurze Toast-/Snackbar-Rückmeldung an. Ohne Provider passiert nichts. */
export function useSnackbar(): ToastFunktion {
  const zeige = useContext(Kontext)
  return zeige ?? (() => undefined)
}

/** Alias für useSnackbar — empfohlener semantischer Aufruf. */
export const useToast = useSnackbar
